/**
 * Main Warehouse Layout Editor - Thin shell component.
 * Orchestrates child components via LayoutEditorContext.
 * Reduced from 951 lines to ~100 lines.
 */

import React from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { LayoutEditorProvider, useLayoutEditor } from './contexts/LayoutEditorContext';
import EditorHeader from './components/EditorHeader';
import EditorSidebar from './components/EditorSidebar';
import EditorToolbar from './components/EditorToolbar';
import Layout2DCanvas from './components/Layout2DCanvas';
import Layout3DScene from './components/Layout3DScene';
import FirstPersonScene from './components/FirstPersonScene';
import PlacementPanel from './components/PlacementPanel';
import PropertiesPanel from './components/PropertiesPanel';
import SelectionStatusBar from './components/SelectionStatusBar';
import QuickLayoutDialog from './components/QuickLayoutDialog';
import ContentsDialog from './components/ContentsDialog';
import MoveConfirmDialog from './components/MoveConfirmDialog';
import LayoutMetricsBar from './components/LayoutMetricsBar';

interface WarehouseLayoutEditorProps {
  warehouseId?: string;
  onBack?: () => void;
}

const EditorContent: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const { state, dispatch } = useLayoutEditor();
  const { t } = useTranslation();

  if (state.loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('manager.modules.warehouseLayout.editor.loadingWarehouseData', {}, 'Laster lagerdata...')}</p>
        </div>
      </div>
    );
  }

  if (!state.layout) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-gray-100">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{t('manager.modules.warehouseLayout.editor.couldNotLoadLayout', {}, 'Kunne ikke laste layout')}</p>
        </div>
      </div>
    );
  }

  return (
    // relative: ContentsDialog-overlayet posisjoneres mot editor-roten (virker i 2D og 3D)
    <div className="relative flex flex-col h-[calc(100vh-64px)] bg-gray-100">
      <EditorHeader onBack={onBack} />

      <div className="flex-1 flex min-h-0 overflow-hidden">
        <EditorSidebar />

        {/* Main canvas/scene area */}
        <div className="flex-1 min-w-0 p-4 flex flex-col">
          {state.error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center gap-2 flex-shrink-0">
              <AlertCircle className="w-5 h-5" />
              {state.error}
              <button
                onClick={() => dispatch({ type: 'CLEAR_ERROR' })}
                className="ml-auto text-red-800 hover:text-red-900"
              >
                &times;
              </button>
            </div>
          )}

          {state.editorState.viewMode === '2d' && <EditorToolbar />}

          <div className="flex-1 min-h-0 rounded-xl overflow-hidden shadow-lg">
            {state.editorState.viewMode === '2d' && <Layout2DCanvas />}
            {state.editorState.viewMode === '3d' && <Layout3DScene />}
            {state.editorState.viewMode === 'first-person' && <FirstPersonScene />}
          </div>
        </div>

        {/* Høyre side: bulk-plassering har forrang, ellers kontekstuelt egenskaps-panel */}
        <PlacementPanel />
        <PropertiesPanel />
      </div>

      <LayoutMetricsBar />
      <SelectionStatusBar />

      {/* Smart Layout Modal */}
      <QuickLayoutDialog />

      {/* Innholdsdialog («hva ligger her?») — reol/enkeltlokasjon, 2D og 3D */}
      <ContentsDialog />

      {/* Bekreftelsesdialog for «Flytt herfra» — åpnes når mål er valgt i 2D */}
      <MoveConfirmDialog />
    </div>
  );
};

const WarehouseLayoutEditor: React.FC<WarehouseLayoutEditorProps> = ({
  warehouseId,
  onBack,
}) => {
  const { user } = useAuth();
  const { currentWarehouse } = useWarehouse();
  const companyNo = user?.companyNo || '';
  const resolvedWarehouseId = warehouseId || currentWarehouse || '1';

  return (
    <LayoutEditorProvider warehouseId={resolvedWarehouseId} companyNo={companyNo}>
      <EditorContent onBack={onBack} />
    </LayoutEditorProvider>
  );
};

export default WarehouseLayoutEditor;
